import { yupResolver } from '@hookform/resolvers/yup';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import { postV1CompaniesCompanyIdDepartments } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import { TextFormField } from '~components/FormFields/TextFormField';
import { CompanyBasic } from '~hooks/useCompany/models';
import { Department } from '~hooks/useDepartment/models';
import connection from '~services/connectionModule';
import { useStores } from '~store';

import {
  DepartmentDto,
  departmentSchema,
  setDepartmentSchemaDefaultValues,
} from './departmentSchema';

interface DepartmentFormProps {
  connectedCompany?: CompanyBasic;
  onClose: () => void;
  onCreated: (department: Department) => void;
}

export default function DepartmentInlineForm({
  connectedCompany,
  onClose,
  onCreated,
}: DepartmentFormProps) {
  const { companyAssetsStore, userStore } = useStores();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const defaultValues = setDepartmentSchemaDefaultValues(userStore);

  if (connectedCompany) {
    defaultValues.company = {
      label: connectedCompany.legalName,
      value: connectedCompany.id,
    };
  }

  const {
    control,
    formState: { errors },
    getValues,
    handleSubmit,
    reset,
    setValue,
  } = useForm<DepartmentDto>({
    resolver: yupResolver(departmentSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });

  const handleCreate = useCallback(async () => {
    try {
      setIsSubmitting(true);

      const formData = getValues();

      const response = await postV1CompaniesCompanyIdDepartments({
        path: { 'company-id': formData.company.value },
        body: { name: formData.name },
      });
      const parsed = Department.parse(response.data.data);

      // Register the new department in the shared store (mirroring the full
      // DepartmentManagement create path) so it remains a selectable option
      // after the dropdown is cleared and reopened — the options are sourced
      // from companyAssetsStore.departments (TRE-15164).
      companyAssetsStore.addDepartment(parsed);

      setIsSubmitting(false);
      reset();
      onCreated(parsed);
    } catch (error) {
      setIsSubmitting(false);
      connection.handleRequestError(
        error,
        t('error_messages.departments.failed_to_create'),
      );
    }
  }, [onCreated]);

  useEffect(() => {
    setValue('company', defaultValues.company);
  }, [defaultValues.company]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(handleCreate)}
      sx={{
        display: 'flex',
        width: '100%',
        flexGrow: 1,
      }}
    >
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <TextFormField
            control={control}
            errors={errors}
            isRequired
            label={`${t('form_fields.name')}`}
            name="name"
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              color="secondary"
              disabled={isSubmitting}
              onClick={onClose}
              type="button"
              variant="outlined"
              sx={{ mr: 2, px: 2 }}
            >
              {t('actions.cancel')}
            </Button>

            <Button
              data-test-id="create-project-btn"
              color="primary"
              disabled={isSubmitting}
              loading={isSubmitting}
              loadingPosition="start"
              startIcon={<></>}
              type="submit"
              variant="contained"
              sx={isSubmitting ? { pl: 5, pr: 2 } : { pr: 2, pl: 2 }}
            >
              {t('actions.create')}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
